import _uniqBy from 'lodash-es/uniqBy';

import type { MetadataFieldValuesPatched } from '@iconik-io/iconik-api/src/schema_patched/metadata';
import {
  type AssetsCustomActionCallbackSchema,
  type AssetsCustomActionSchemaWithId,
  httpGetCustomActions,
  httpPostCustomActionsByContextByActionIdCallback,
  httpPostCustomActionsSharedByContextByActionIdCallback,
} from '@iconik-io/iconik-api/src/services/assets';

import { infoMessage, successMessage } from '../messages/thunks';
import { type AppDispatch, createAppThunk } from '../types';
import { setCustomActions } from './slice';
export type CustomActionForCallback = {
  label: string;
  id: string;
  context: string;
  type?: string;
  metadataView?: string | null;
};

export type CustomActionForCallbackData = {
  collection_ids?: string[];
  asset_ids?: string[];
  saved_search_ids?: string[];
  from_collection_id?: string;
};

export type CustomActionForCallbackDataWithMetadata =
  CustomActionForCallbackData & {
    metadata_values?: MetadataFieldValuesPatched;
  };

export const getCustomActions = createAppThunk(() => (dispatch, getState) => {
  return dispatch(httpGetCustomActions()).then((response) => {
    const customActions = getState().customActions?.customActions || [];
    // biome-ignore lint/plugin: API boundary, the list endpoint is generated without the id that every returned custom action carries.
    const newCustomActions = (response.body.objects ??
      []) as AssetsCustomActionSchemaWithId[];
    return dispatch(
      setCustomActions(_uniqBy([...customActions, ...newCustomActions], 'id')),
    );
  });
});

export const customActionCallbackRequest = (
  context: string,
  id: string,
  data?: CustomActionForCallbackDataWithMetadata,
) => {
  const actionFromShare =
    context === 'SHARED_ASSET' ||
    context === 'SHARED_COLLECTION' ||
    context === 'SHARED_PLAYLIST';
  // biome-ignore lint/plugin: the callback endpoints declare the generated schema, but callers pass the app's richer CustomActionForCallbackDataWithMetadata.
  const callbackData = data as AssetsCustomActionCallbackSchema;
  return actionFromShare
    ? httpPostCustomActionsSharedByContextByActionIdCallback(
        context,
        id,
        callbackData,
      )
    : httpPostCustomActionsByContextByActionIdCallback(
        context,
        id,
        callbackData,
      );
};

const redirectHttp = (url: string) => {
  const popupWindow = window.open(url, '_blank');
  try {
    // This raises an exception in Safari
    popupWindow?.focus();
  } catch (error) {
    infoMessage({
      text: 'Click here to open the link: ' + url,
      timeout: 10000,
    });
    // biome-ignore lint/suspicious/noConsole: logging error
    console.error('Failed to open redirect URL', error);
  }
};

export const triggerCustomAction =
  (
    action: CustomActionForCallback,
    data?: CustomActionForCallbackDataWithMetadata,
  ) =>
  (dispatch: AppDispatch) => {
    return dispatch(
      customActionCallbackRequest(action.context, action.id, data),
    ).then((response) => {
      const url = response.body.redirect_url;
      if (url) {
        if (/^https?:\/\//.test(url)) {
          // Need to open in new tab to avoid popup blockers
          redirectHttp(url);
        } else {
          window.location.href = url;
        }
      } else {
        dispatch(successMessage({ text: `${action.label} triggered` }));
      }
    });
  };
